import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import {
    useActivateCapitalCallMutation,
    useDeleteCapitalCallMutation,
    useDownloadCapitalCallGeneratedDocumentsMutation,
    useSendCapitalCallDocumentsMutation,
    useSimulateCapitalCallMutation,
    useTestCapitalCallMutation,
    useTraceCapitalCallMutation,
    useUpdateCapitalCallMutation,
} from 'api/main';
import { notificationCreated } from 'reducers/appNotification';
import { capitalCallStatus } from 'utils/constants';
import { parsePercentage, parseSum } from 'utils/helpers';
import { useDialog } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import {
    ActionsMenu,
    ConfirmDialog,
    CustomDatePicker,
    CustomMenuItem,
    CustomTextField,
    Loader,
    StyledTableRow,
    SuccessMessage,
} from 'components';
import {
    Box,
    FormHelperText,
    IconButton,
    InputAdornment,
    TableCell,
    Typography,
} from '@mui/material';
import {
    ContentSave,
    Pencil,
    Close,
    Delete,
    FileExcel,
    EmailArrowRightOutline,
    FileDownloadOutline,
    BookmarkCheck,
    PercentCircleOutline,
    FileSend,
} from 'mdi-material-ui';

const MenuEdit = ({ classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <Pencil className={classes.actionIcons} /> {texts.EDIT}
    </CustomMenuItem>
);

const MenuDelete = ({ classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <Delete className={classes.actionIcons} /> {texts.SUPPRIMER}
    </CustomMenuItem>
);

const MenuSimulate = ({ loading, classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <Loader size={20} marginTop={0} loading={loading} color="inherit">
            <FileExcel className={classes.actionIcons} /> {texts.SIMULATE}
        </Loader>
    </CustomMenuItem>
);

const MenuTrace = ({ loading, classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <Loader size={20} marginTop={0} loading={loading} color="inherit">
            <FileExcel className={classes.actionIcons} /> {texts.TRACE}
        </Loader>
    </CustomMenuItem>
);

const MenuTest = ({ classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <BookmarkCheck className={classes.actionIcons} /> {texts.TEST}
    </CustomMenuItem>
);

const MenuActivate = ({ classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <EmailArrowRightOutline className={classes.actionIcons} /> {texts.ACTIVATE}
    </CustomMenuItem>
);

const MenuPremium = ({ classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <PercentCircleOutline className={classes.actionIcons} /> {texts.ENTER_SUBSCRIPTION_PREMIUM}
    </CustomMenuItem>
);
const MenuDownload = ({ loading, classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <Loader size={20} marginTop={0} loading={loading} color="inherit">
            <FileDownloadOutline className={classes.actionIcons} />{' '}
        </Loader>
        {texts.DOWNLOAD_GENERATED_DOCUMENTS}
    </CustomMenuItem>
);
const MenuSendDocs = ({ classes, ...props }) => (
    <CustomMenuItem classes={classes} {...props}>
        <FileSend className={classes.actionIcons} /> {texts.SEND_GENERATED_DOCUMENTS}
    </CustomMenuItem>
);

const EditItem = React.forwardRef((props, ref) => <MenuEdit ref={ref} {...props} />);

const CapitalCallRow = ({
    capitalCall,
    index,
    array,
    selectedFund,
    amountError,
    setAmountError,
    total,
    totalCalledPercentage,
    minActivationDate,
}) => {
    const dispatch = useDispatch();
    const [menuOpen, setMenuOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmDialogType, setConfirmDialogType] = useState(null);
    const [premiumOpen, setPremiumOpen] = useState(false);
    const [premium, setPremium] = useState('');
    const [premiumError, setPremiumError] = useState('');

    const { open: sendDocsOpen, onClose: onSendDocsClose, onOpen: onSendDocsOpen } = useDialog();

    const [simulateCapitalCall, { isLoading: simulationLoading, isSuccess: simulationSuccess }] =
        useSimulateCapitalCallMutation();
    const [updateCapitalCall, { isLoading: updateLoading, isSuccess: updateSuccess }] =
        useUpdateCapitalCallMutation();
    const [deleteCapitalCall, { isLoading: deleteLoading }] = useDeleteCapitalCallMutation();
    const [testCapitalCall, { isLoading: testLoading, isSuccess: testSuccess }] =
        useTestCapitalCallMutation();
    const [activateCapitalCall, { isLoading: activateLoading, isSuccess: activateSuccess }] =
        useActivateCapitalCallMutation();
    const [
        downloadCapitalCallGeneratedDocuments,
        { isLoading: downloadLoading, isError: downloadError },
    ] = useDownloadCapitalCallGeneratedDocumentsMutation();
    const [traceCapitalCall, { isLoading: traceLoading }] = useTraceCapitalCallMutation();
    const [
        sendCapitalCallDocuments,
        { isLoading: sendLoading, isSuccess: sendSuccess, reset: resetSendDocs },
    ] = useSendCapitalCallDocumentsMutation();

    const isLast = index === array.length - 1;
    const isEditing = isLast && capitalCall.id && capitalCall.id === editing?.id;
    const isActive = capitalCall.status === capitalCallStatus.ACTIVATED;
    const isCompleted = capitalCall.status === capitalCallStatus.COMPLETED;
    // TODO reinstate the date validations; they are ignored temporarily
    // const canEdit = moment(capitalCall.issueDate).isSameOrAfter(moment().add(15, 'day'), 'day');
    const contentSaveEnabled = useMemo(
        () => (editing?.issueSum > 0 && editing?.reason?.trim().length > 0) || updateLoading,
        [editing, updateLoading]
    );

    const handleEdit = useCallback(
        capitalCall => () => {
            setMenuOpen(false);
            setEditing({
                ...capitalCall,
                percentage: parsePercentage(capitalCall.percentage).toString(),
            });
        },
        []
    );

    const handleSimulate = useCallback(() => {
        simulateCapitalCall({ investmentFundId: selectedFund.id });
    }, [simulateCapitalCall, selectedFund]);

    const handleActivate = useCallback(() => {
        activateCapitalCall({ investmentFundId: selectedFund.id });
    }, [selectedFund, activateCapitalCall]);

    const handleTest = useCallback(() => {
        testCapitalCall({ investmentFundId: selectedFund.id });
    }, [selectedFund, testCapitalCall]);

    const handleOpenConfirm = useCallback(
        type => () => {
            setConfirmDialogType(type);
            setConfirmDialogOpen(true);
            setMenuOpen(false);
        },
        []
    );

    const handleOpenPremiumDialog = useCallback(() => {
        setPremiumOpen(true);
    }, []);

    const handleSavePremium = useCallback(() => {
        if (!premium) {
            setPremiumError(texts.NUMBER_FIELD_ERROR);
            return;
        }
        if (premium < 0 || premium > 100) {
            setPremiumError(texts.PERCENTAGE_VALUE);
            return;
        }
        updateCapitalCall({ id: capitalCall?.id, premium });
    }, [premium, updateCapitalCall, capitalCall]);

    const handleDelete = useCallback(
        id => () => {
            deleteCapitalCall(id);
            setMenuOpen(false);
        },
        [deleteCapitalCall]
    );

    const handleDateChange = useCallback(
        newDate => setEditing(oldValue => ({ ...oldValue, issueDate: newDate })),
        []
    );

    const handleChange = useCallback(
        key => e => {
            const { value } = e.target;
            setEditing(oldValue => ({
                ...oldValue,
                [key]: value,
                issueSum:
                    key === 'percentage' ? ((+value / 100) * total).toFixed(2) : oldValue.issueSum,
            }));
        },
        [total]
    );

    const handleChangePremium = useCallback(e => {
        setPremium(e.target.value);
    }, []);

    const handleClosePremium = useCallback(() => {
        setPremium('');
        setPremiumOpen(false);
    }, []);

    const handleUpdate = useCallback(() => {
        const percentage = +editing?.percentage;
        const oldPercentage = +array.find(fc => fc.id === editing.id).percentage;
        if (!percentage || percentage > 100 - totalCalledPercentage + oldPercentage) {
            setAmountError(
                texts.FUND_CALL_PERCENTAGE_ERROR(
                    (100 - totalCalledPercentage + oldPercentage).toFixed(3)
                )
            );
            return;
        }
        setAmountError('');
        updateCapitalCall({
            ...editing,
            premium: editing.premium.toString(),
            issueDate: moment(editing.issueDate).utcOffset(0).startOf('day'),
        });
    }, [editing, totalCalledPercentage, updateCapitalCall, array, setAmountError]);

    const handleDownloadGeneratedDocuments = useCallback(() => {
        downloadCapitalCallGeneratedDocuments(capitalCall.id);
    }, [capitalCall, downloadCapitalCallGeneratedDocuments]);
    const handleSendGeneratedDocuments = useCallback(() => {
        sendCapitalCallDocuments(capitalCall.id);
    }, [capitalCall, sendCapitalCallDocuments]);

    useEffect(() => {
        if (simulationSuccess) {
            setMenuOpen(false);
        }
    }, [simulationSuccess]);

    useEffect(() => {
        if (updateSuccess) {
            setEditing(null);
        }
    }, [updateSuccess, handleClosePremium]);

    useEffect(() => {
        if (activateSuccess || testSuccess) {
            setConfirmDialogOpen(false);
        }
    }, [activateSuccess, testSuccess]);

    useEffect(() => {
        if (sendSuccess && !sendDocsOpen) {
            resetSendDocs();
        }
    }, [resetSendDocs, sendSuccess, sendDocsOpen]);

    useEffect(() => {
        if (downloadError) {
            dispatch(notificationCreated('FAILED_DOCUMENT_DOWNLOAD'));
        }
    }, [dispatch, downloadError]);

    return (
        <StyledTableRow key={index}>
            <TableCell align="center">
                {isEditing ? (
                    <CustomDatePicker
                        controlSx={{ margin: 0 }}
                        value={editing?.issueDate}
                        onChange={handleDateChange}
                        minDate={moment().add(1, 'd')}
                    />
                ) : (
                    moment(capitalCall.issueDate).format('L')
                )}
            </TableCell>
            <TableCell align="center">
                {isEditing ? (
                    <CustomTextField
                        formControlProps={{ sx: { margin: 0 } }}
                        value={editing?.percentage}
                        onChange={handleChange('percentage')}
                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                ) : (
                    `${parsePercentage(capitalCall.percentage)}\xa0%`
                )}
            </TableCell>
            <TableCell align="center">
                {isEditing ? (
                    <CustomTextField
                        disabled
                        type="number"
                        formControlProps={{ sx: { margin: 0 } }}
                        value={editing?.issueSum}
                        error={amountError}
                        showError={false}
                    />
                ) : (
                    parseSum(capitalCall.issueSum)
                )}
            </TableCell>
            <TableCell align="center">{parseSum(capitalCall.amountIssuedFor)}</TableCell>
            <TableCell align="center">
                {isEditing ? (
                    <CustomTextField
                        label={texts.FUND_CALLS_REASON}
                        multiline
                        rows={3}
                        value={editing?.reason}
                        onChange={handleChange('reason')}
                    />
                ) : (
                    capitalCall.reason
                )}
            </TableCell>
            <TableCell align="center">
                {capitalCall.premium ? capitalCall.premium + ' %' : '-'}
            </TableCell>
            <TableCell align="center">
                {isEditing ? (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <IconButton
                            disableRipple
                            sx={{ '&:hover': { color: 'primary.main' } }}
                            size="small"
                            onClick={handleUpdate}
                            disabled={!contentSaveEnabled}
                        >
                            <Loader loading={updateLoading} size={20} marginTop={0}>
                                <ContentSave />
                            </Loader>
                        </IconButton>
                        <IconButton disableRipple size="small" onClick={() => setEditing(null)}>
                            <Close />
                        </IconButton>
                    </Box>
                ) : (
                    <React.Fragment>
                        {isCompleted ? (
                            <ActionsMenu
                                autoClose
                                open={menuOpen}
                                handleOpen={(value = true) => setMenuOpen(value)}
                            >
                                <MenuTrace
                                    loading={traceLoading}
                                    onClick={() => traceCapitalCall(capitalCall.id)}
                                />
                                <MenuDownload
                                    loading={downloadLoading}
                                    onClick={handleDownloadGeneratedDocuments}
                                />
                                <MenuPremium onClick={handleOpenPremiumDialog} />
                                <MenuSendDocs onClick={onSendDocsOpen} />
                            </ActionsMenu>
                        ) : (
                            <ActionsMenu
                                controlled
                                open={menuOpen}
                                handleOpen={(value = true) => setMenuOpen(value)}
                            >
                                <EditItem disabled={isActive} onClick={handleEdit(capitalCall)} />
                                <MenuDelete
                                    disabled={isActive || deleteLoading}
                                    onClick={handleDelete(capitalCall.id)}
                                />
                                <MenuSimulate
                                    loading={simulationLoading}
                                    disabled={isActive}
                                    onClick={handleSimulate}
                                />
                                <MenuTest disabled={isActive} onClick={handleOpenConfirm('test')} />
                                <MenuActivate
                                    disabled={isActive}
                                    onClick={handleOpenConfirm('activate')}
                                />
                            </ActionsMenu>
                        )}
                    </React.Fragment>
                )}
                <ConfirmDialog
                    open={confirmDialogOpen}
                    loading={testLoading || activateLoading}
                    confirmText={texts.CONFIRMER}
                    cancelText={texts.CANCEL}
                    title={texts.EXIGIBILITY_DATE}
                    handleClose={() => setConfirmDialogOpen(false)}
                    handleConfirm={confirmDialogType === 'activate' ? handleActivate : handleTest}
                    disableConfirm={minActivationDate.isSameOrAfter(
                        moment(capitalCall.issueDate),
                        'd'
                    )}
                >
                    <Box textAlign="center">
                        {minActivationDate.isSameOrAfter(moment(capitalCall.issueDate), 'd') && (
                            <FormHelperText error>{texts.DATE_MORE_THAN}</FormHelperText>
                        )}
                        <CustomTextField
                            disabled
                            formControlProps={{ sx: { margin: 0 } }}
                            value={moment(capitalCall.issueDate).format('L')}
                        />
                    </Box>
                </ConfirmDialog>
                <ConfirmDialog
                    open={premiumOpen}
                    loading={updateLoading}
                    confirmText={texts.SAVE}
                    cancelText={texts.CANCEL}
                    title={texts.ENTER_SUBSCRIPTION_PREMIUM}
                    handleClose={handleClosePremium}
                    handleConfirm={handleSavePremium}
                >
                    <CustomTextField
                        formatNumber
                        label={texts.SUBSCRIPTION_PREMIUM}
                        formControlProps={{ sx: { margin: 0 } }}
                        value={premium}
                        onChange={handleChangePremium}
                        error={premiumError}
                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                    {updateSuccess && <SuccessMessage message={texts.DATA_SAVED} />}
                </ConfirmDialog>
                <ConfirmDialog
                    open={sendDocsOpen}
                    loading={sendLoading}
                    confirmText={texts.CONFIRMER}
                    cancelText={texts.CANCEL}
                    title={texts.SEND_GENERATED_DOCUMENTS}
                    handleClose={onSendDocsClose}
                    disableConfirm={sendSuccess}
                    handleConfirm={handleSendGeneratedDocuments}
                >
                    <Typography>{texts.SEND_GENERATED_DOCUMENTS_CONTENT}</Typography>
                    {sendSuccess && (
                        <SuccessMessage message={texts.SEND_GENERATED_DOCUMENTS_SUCCESS} />
                    )}
                </ConfirmDialog>
            </TableCell>
        </StyledTableRow>
    );
};

CapitalCallRow.propTypes = {
    capitalCall: PropTypes.object,
    index: PropTypes.number,
    array: PropTypes.array,
    selectedFund: PropTypes.object,
    amountError: PropTypes.string,
    setAmountError: PropTypes.func,
    total: PropTypes.number,
    totalCalledPercentage: PropTypes.number,
    minActivationDate: PropTypes.object,
};

export default CapitalCallRow;
